/**
 * Buffer Optimization System
 * Implements strategies to prevent buffer underruns identified in audio dropout analysis
 */

import { audioPerformanceLogger } from './audioPerformanceLogger';

class BufferOptimizer {
  constructor() {
    this.isOptimizing = false;
    this.preloadBuffer = new Map(); // Cache for preloaded audio data
    this.bufferConfig = {
      // Increased buffer sizes based on log analysis showing 0.4s buffer ahead time failures
      targetBufferAhead: 8.0,     // Target 8 seconds ahead (was causing issues at <1s)
      minBufferAhead: 4.0,        // Minimum 4 seconds before warning
      criticalBufferAhead: 2.0,   // Critical threshold requiring immediate action
      maxPreloadTracks: 3,        // Number of tracks to preload
      bufferCheckInterval: 1000,  // Check buffer health every second
      recoveryTimeout: 5000       // Timeout for buffer recovery attempts
    };
    this.bufferMonitor = null;
    this.recoveryAttempts = 0;
    this.maxRecoveryAttempts = 3;
  }

  /**
   * Initialize buffer optimization for an audio element
   */
  initializeBufferOptimization(audioElement, playlist = []) {
    if (this.isOptimizing) {
      this.stopBufferOptimization();
    }

    this.isOptimizing = true;
    this.audioElement = audioElement;
    this.playlist = playlist;
    this.recoveryAttempts = 0;

    audioPerformanceLogger.info('buffer', 'Buffer optimization system initialized', {
      config: this.bufferConfig,
      playlistLength: playlist.length
    });

    // Apply HTML5 audio optimizations
    this.applyAudioElementOptimizations();

    // Start continuous buffer monitoring
    this.startBufferMonitoring();

    // Begin background preloading
    this.startBackgroundPreloading();

    return true;
  }

  /**
   * Apply optimizations to the HTML5 audio element
   */
  applyAudioElementOptimizations() {
    if (!this.audioElement) return;

    // Set preload to 'auto' for aggressive buffering
    this.audioElement.preload = 'auto';

    // Set buffer size hints (browser-specific optimizations)
    if (this.audioElement.buffered) {
      // Some browsers support buffer size hints
      this.audioElement.crossOrigin = 'anonymous'; // Enable CORS for better buffering
    }

    // Add buffer event listeners for real-time optimization
    this.addBufferEventListeners();

    audioPerformanceLogger.debug('buffer', 'Audio element optimizations applied', {
      preload: this.audioElement.preload,
      crossOrigin: this.audioElement.crossOrigin,
      readyState: this.audioElement.readyState
    });
  }

  /**
   * Add event listeners for buffer-related events
   */
  addBufferEventListeners() {
    if (!this.audioElement) return;

    // Listen for buffer-related events that indicate potential issues
    const bufferEvents = [
      'waiting',      // Playback stopped due to lack of data
      'stalled',      // Download stalled
      'suspend',      // Download suspended
      'progress',     // Download progressing
      'canplay',      // Enough data to start playing
      'canplaythrough' // Enough data to play without interruption
    ];

    bufferEvents.forEach(eventType => {
      this.audioElement.addEventListener(eventType, (event) => {
        this.handleBufferEvent(eventType, event);
      });
    });
  }

  /**
   * Handle buffer-related events and take corrective action
   */
  handleBufferEvent(eventType, event) {
    const currentTime = this.audioElement.currentTime;
    const buffered = this.audioElement.buffered;
    const bufferAhead = this.calculateBufferAhead();

    audioPerformanceLogger.debug('buffer', `Buffer event: ${eventType}`, {
      currentTime,
      bufferAhead,
      readyState: this.audioElement.readyState,
      networkState: this.audioElement.networkState
    });

    switch (eventType) {
      case 'waiting':
      case 'stalled':
        this.handleBufferUnderrun();
        break;
      
      case 'progress':
        this.optimizeBuffering();
        break;
      
      case 'canplay':
      case 'canplaythrough':
        this.recoveryAttempts = 0; // Reset recovery attempts on successful buffering
        break;
    }
  }

  /**
   * Calculate how much audio is buffered ahead of current playback position
   */
  calculateBufferAhead() {
    if (!this.audioElement || !this.audioElement.buffered) return 0;

    const currentTime = this.audioElement.currentTime;
    const buffered = this.audioElement.buffered;

    for (let i = 0; i < buffered.length; i++) {
      const start = buffered.start(i);
      const end = buffered.end(i);
      
      if (currentTime >= start && currentTime <= end) {
        return end - currentTime;
      }
    }

    return 0;
  }

  /**
   * Handle buffer underrun situations (TEMPORARILY DISABLED TO PREVENT FLICKER)
   */
  handleBufferUnderrun() {
    this.recoveryAttempts++;
    
    audioPerformanceLogger.warn('buffer', 'Buffer underrun detected', {
      attempt: this.recoveryAttempts,
      maxAttempts: this.maxRecoveryAttempts,
      currentTime: this.audioElement.currentTime,
      bufferAhead: this.calculateBufferAhead()
    });

    // TEMPORARILY DISABLED: Aggressive recovery causing play/pause loops
    // TODO: Implement gentler recovery strategies
    
    if (this.recoveryAttempts <= this.maxRecoveryAttempts) {
      // Strategy 1: DISABLED - Force buffer refresh (was causing flicker)
      // this.forceBufferRefresh();
      
      // Strategy 2: DISABLED - Reduce quality temporarily (if supported)
      // this.temporaryQualityReduction();
      
      // Strategy 3: Still active - Gentle preloading only
      this.aggressivePreload();
    } else {
      audioPerformanceLogger.error('buffer', 'Maximum recovery attempts reached', {
        attempts: this.recoveryAttempts,
        recommendation: 'Consider reducing audio quality or checking network connection'
      });
    }
  }

  /**
   * Force a buffer refresh by briefly pausing and resuming (DISABLED - WAS CAUSING FLICKER)
   */
  forceBufferRefresh() {
    // COMPLETELY DISABLED: This function was causing infinite play/pause loops
    audioPerformanceLogger.debug('buffer', 'Buffer refresh requested but disabled to prevent flicker');
    return;
    
    /* ORIGINAL PROBLEMATIC CODE DISABLED:
    if (!this.audioElement) return;

    const wasPlaying = !this.audioElement.paused;
    const currentTime = this.audioElement.currentTime;

    audioPerformanceLogger.debug('buffer', 'Forcing buffer refresh', {
      wasPlaying,
      currentTime
    });

    // Brief pause to allow buffer recovery
    this.audioElement.pause();
    
    setTimeout(() => {
      if (this.audioElement) {
        this.audioElement.currentTime = currentTime; // Maintain position
        if (wasPlaying) {
          this.audioElement.play().catch(err => {
            audioPerformanceLogger.error('buffer', 'Failed to resume after buffer refresh', {
              error: err.message
            });
          });
        }
      }
    }, 100); // Brief 100ms pause
    */
  }

  /**
   * Temporarily reduce quality to improve buffering
   */
  temporaryQualityReduction() {
    // Note: This would require server-side support for multiple quality streams
    // For now, we'll log the recommendation
    audioPerformanceLogger.info('buffer', 'Quality reduction recommended', {
      note: 'Consider implementing multiple quality streams for better buffer performance'
    });
  }

  /**
   * Aggressive preloading of upcoming audio data
   */
  aggressivePreload() {
    if (!this.playlist || this.playlist.length === 0) return;

    const currentIndex = this.getCurrentTrackIndex();
    const nextTracks = this.playlist.slice(currentIndex + 1, currentIndex + 3); // Next 2 tracks

    nextTracks.forEach((track, index) => {
      this.preloadTrack(track, `next_${index}`);
    });
  }

  /**
   * Start continuous buffer monitoring (REDUCED FREQUENCY)
   */
  startBufferMonitoring() {
    // REDUCED frequency to prevent excessive triggering
    this.bufferMonitor = setInterval(() => {
      if (!this.audioElement || this.audioElement.paused) return;

      const bufferAhead = this.calculateBufferAhead();
      
      // TEMPORARILY DISABLED: Critical buffer handling causing loops
      // if (bufferAhead < this.bufferConfig.criticalBufferAhead) {
      //   audioPerformanceLogger.warn('buffer', 'Critical buffer level detected', {
      //     bufferAhead,
      //     threshold: this.bufferConfig.criticalBufferAhead
      //   });
      //   this.handleBufferUnderrun();
      // } else 
      
      if (bufferAhead < this.bufferConfig.minBufferAhead) {
        audioPerformanceLogger.debug('buffer', 'Low buffer level detected', {
          bufferAhead,
          threshold: this.bufferConfig.minBufferAhead
        });
        // GENTLE optimization only
        this.gentleOptimizeBuffering();
      }
    }, this.bufferConfig.bufferCheckInterval * 3); // 3x less frequent (every 3 seconds)
  }

  /**
   * Gentle buffer optimization (non-disruptive)
   */
  gentleOptimizeBuffering() {
    if (!this.audioElement) return;

    const bufferAhead = this.calculateBufferAhead();
    const networkState = this.audioElement.networkState;

    // Only log, don't disrupt playback
    audioPerformanceLogger.debug('buffer', 'Gentle buffer monitoring', {
      bufferAhead,
      target: this.bufferConfig.targetBufferAhead,
      networkState,
      readyState: this.audioElement.readyState
    });
    
    // Only preload next tracks, don't manipulate current playback
    this.aggressivePreload();
  }

  /**
   * Optimize buffering based on current conditions (DISABLED)
   */
  optimizeBuffering() {
    // DISABLED: This was causing play/pause loops
    audioPerformanceLogger.debug('buffer', 'Buffer optimization called but disabled to prevent flicker');
    return;
    
    /* ORIGINAL CODE DISABLED:
    if (!this.audioElement) return;

    const bufferAhead = this.calculateBufferAhead();
    const networkState = this.audioElement.networkState;

    // If network is idle and buffer is low, trigger more aggressive loading
    if (networkState === 1 && bufferAhead < this.bufferConfig.targetBufferAhead) { // NETWORK_IDLE
      this.audioElement.load(); // Force reload to restart buffering
      
      audioPerformanceLogger.debug('buffer', 'Triggered buffer optimization', {
        bufferAhead,
        target: this.bufferConfig.targetBufferAhead,
        networkState
      });
    }
    */
  }

  /**
   * Start background preloading of upcoming tracks
   */
  startBackgroundPreloading() {
    if (!this.playlist || this.playlist.length <= 1) return;

    const currentIndex = this.getCurrentTrackIndex();
    const tracksToPreload = Math.min(
      this.bufferConfig.maxPreloadTracks,
      this.playlist.length - currentIndex - 1
    );

    for (let i = 1; i <= tracksToPreload; i++) {
      const nextIndex = currentIndex + i;
      if (nextIndex < this.playlist.length) {
        const track = this.playlist[nextIndex];
        this.preloadTrack(track, `preload_${i}`);
      }
    }
  }

  /**
   * Preload a specific track
   */
  preloadTrack(track, identifier) {
    if (!track || !track.file) return;

    // Avoid duplicate preloads
    if (this.preloadBuffer.has(identifier)) return;

    const audio = new Audio();
    audio.preload = 'auto';
    audio.crossOrigin = 'anonymous';
    
    audio.addEventListener('canplaythrough', () => {
      audioPerformanceLogger.debug('buffer', 'Track preloaded successfully', {
        track: track.title || 'Unknown',
        identifier
      });
    });

    audio.addEventListener('error', (err) => {
      audioPerformanceLogger.warn('buffer', 'Track preload failed', {
        track: track.title || 'Unknown',
        identifier,
        error: err.message || err.toString()
      });
    });

    // Handle different track.file types properly
    try {
      if (track.file instanceof File) {
        // For File objects, create object URL
        const objectURL = URL.createObjectURL(track.file);
        audio.src = objectURL;
        
        // Clean up object URL when audio is no longer needed
        audio.addEventListener('loadeddata', () => {
          // Keep the URL active while preloaded
        });
        
        // Store cleanup function
        audio._cleanup = () => {
          URL.revokeObjectURL(objectURL);
        };
      } else if (typeof track.file === 'string') {
        // For string URLs, use directly
        audio.src = track.file;
      } else {
        audioPerformanceLogger.warn('buffer', 'Invalid track file type', {
          track: track.title || 'Unknown',
          fileType: typeof track.file,
          identifier
        });
        return;
      }
      
      this.preloadBuffer.set(identifier, audio);
    } catch (error) {
      audioPerformanceLogger.error('buffer', 'Failed to set audio source for preload', {
        track: track.title || 'Unknown',
        identifier,
        error: error.message
      });
    }
  }

  /**
   * Get current track index in playlist
   */
  getCurrentTrackIndex() {
    if (!this.audioElement || !this.playlist) return 0;
    
    const currentSrc = this.audioElement.src;
    
    // For File objects, compare by name since URLs will be different
    return this.playlist.findIndex(track => {
      if (!track.file) return false;
      
      if (track.file instanceof File) {
        // For File objects, check if the current src is an object URL 
        // and the track name matches (basic heuristic)
        return currentSrc.startsWith('blob:') && 
               track.file.name && 
               (track.title?.includes(track.file.name.replace(/\.[^/.]+$/, "")) || 
                currentSrc.includes(track.file.name));
      } else if (typeof track.file === 'string') {
        // For string URLs, use includes check
        return currentSrc.includes(track.file);
      }
      
      return false;
    }) || 0;
  }

  /**
   * Stop buffer optimization
   */
  stopBufferOptimization() {
    this.isOptimizing = false;

    if (this.bufferMonitor) {
      clearInterval(this.bufferMonitor);
      this.bufferMonitor = null;
    }

    // Clean up preload cache
    this.preloadBuffer.forEach(audio => {
      // Clean up object URLs if they exist
      if (audio._cleanup) {
        audio._cleanup();
      }
      audio.src = '';
      audio.load();
    });
    this.preloadBuffer.clear();

    audioPerformanceLogger.info('buffer', 'Buffer optimization stopped');
  }

  /**
   * Get current buffer status
   */
  getBufferStatus() {
    if (!this.audioElement) return null;

    return {
      bufferAhead: this.calculateBufferAhead(),
      readyState: this.audioElement.readyState,
      networkState: this.audioElement.networkState,
      isOptimizing: this.isOptimizing,
      preloadedTracks: this.preloadBuffer.size,
      recoveryAttempts: this.recoveryAttempts
    };
  }
}

// Export singleton instance
export const bufferOptimizer = new BufferOptimizer();
